// src/Components/UserActivities/EfficiencyDownloadModal.jsx

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Typography,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import { PictureAsPdf as PdfIcon, TableChart as ExcelIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import CustomMonthYearIndividualDatePicker from '../Widgets/CustomDates/CustomMonthYearIndividualDatePicker/CustomMonthYearIndividualDatePicker';
import UserActivityService from '../../Services/userActivity.service.js';

export default function EfficiencyDownloadModal({ open, onClose, direction, user, token, reportType = 'employee' }) {
    const [date, setDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [selectedReportType, setSelectedReportType] = useState(reportType);
    const { t, i18n } = useTranslation();

    const handleDateChangeNew = (newValue, type) => {
        const d = newValue;
        const updated =
            type === 'year'
                ? new Date(Date.UTC(d.getFullYear(), date.getMonth(), 1))
                : new Date(Date.UTC(date.getFullYear(), d.getMonth(), 1));

        setDate(updated);
    };

    const handleReportTypeChange = (event, newReportType) => {
        if (newReportType !== null) {
            setSelectedReportType(newReportType);
        }
    };

    const handleDownloadPdf = async () => {
        setLoading(true);
        try {
            const year = date.getFullYear();
            const month = date.getMonth() + 1; // Convert to 1-indexed for API

            // Create request payload matching the backend DTO
            const requestPayload = {
                AccountantId: user.accountantId,
                StartUtc: new Date(Date.UTC(year, month - 1, 1)).toISOString(),
                EndUtc: new Date(Date.UTC(year, month, 0, 23, 59, 59)).toISOString(),
                SubAccountantIds: [],
                CaseIds: [],
            };

            const response = selectedReportType === 'employee' 
                ? await UserActivityService.GetEmployeeEfficiencyReportHtml(token, requestPayload)
                : await UserActivityService.GetBusinessEfficiencyReportHtml(token, requestPayload);

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                const paddedMonth = String(month).padStart(2, '0');
                const reportTypeText = selectedReportType === 'employee' ? 'EmployeeEfficiency' : 'BusinessEfficiency';
                a.download = `${reportTypeText}_${year}_${paddedMonth}.pdf`;

                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                Swal.fire({
                    title: t('Success') || 'Success!',
                    text: t('ReportDownloadedSuccessfully') || 'Report downloaded successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                throw new Error('Failed to generate PDF report');
            }
        } catch (err) {
            console.error("PDF download failed:", err);
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('FailedToDownloadReport') || 'Failed to download report. Please try again.',
                icon: 'error',
                confirmButtonText: t('OK') || 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        setLoading(true);
        try {
            const year = date.getFullYear();
            const month = date.getMonth() + 1; // Convert to 1-indexed for API

            // Create request payload matching the backend DTO
            const requestPayload = {
                AccountantId: user.accountantId,
                StartUtc: new Date(Date.UTC(year, month - 1, 1)).toISOString(),
                EndUtc: new Date(Date.UTC(year, month, 0, 23, 59, 59)).toISOString(),
                SubAccountantIds: [],
                CaseIds: [],
            };

            const response = selectedReportType === 'employee' 
                ? await UserActivityService.GetEmployeeEfficiencyReportExcel(token, requestPayload)
                : await UserActivityService.GetBusinessEfficiencyReportExcel(token, requestPayload);

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                const paddedMonth = String(month).padStart(2, '0');
                const reportTypeText = selectedReportType === 'employee' ? 'EmployeeEfficiency' : 'BusinessEfficiency';
                a.download = `${reportTypeText}_${year}_${paddedMonth}.xlsx`;

                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                Swal.fire({
                    title: t('Success') || 'Success!',
                    text: t('ReportDownloadedSuccessfully') || 'Report downloaded successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                throw new Error('Failed to generate Excel report');
            }
        } catch (err) {
            console.error("Excel download failed:", err);
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('FailedToDownloadReport') || 'Failed to download report. Please try again.',
                icon: 'error',
                confirmButtonText: t('OK') || 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir={direction}>
            <DialogTitle
                dir={direction}
                sx={{
                    background: 'linear-gradient(135deg, #5b6ee0 0%, #6f45a0 100%)',
                    color: 'white',
                    py: 2
                }}>
                {t('DownloadEfficiencyReport') || 'Download Efficiency Report'}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{
                        color: 'text.secondary',
                        mb: 2,
                        textAlign: direction === 'rtl' ? 'right' : 'left'
                    }}>
                        {t('SelectMonthForEfficiencyReport') || 'Select the month and year for the efficiency report. The report will include efficiency data for the accountant and all sub-accountants.'}
                    </Typography>
                </Box>

                {/* Report Type Selection */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                    <ToggleButtonGroup
                        value={selectedReportType}
                        exclusive
                        onChange={handleReportTypeChange}
                        aria-label="report type"
                        size="small"
                    >
                        <ToggleButton value="employee" aria-label="employee efficiency">
                            {t('EmployeeEfficiency') || 'Employee Efficiency'}
                        </ToggleButton>
                        <ToggleButton value="business" aria-label="business efficiency">
                            {t('BusinessEfficiency') || 'Business Efficiency'}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <CustomMonthYearIndividualDatePicker
                        year={date}
                        month={date}
                        onYearChange={(newYear) => handleDateChangeNew(newYear, 'year')}
                        onMonthChange={(newMonth) => handleDateChangeNew(newMonth, 'month')}
                        labelYear={t('Year')}
                        labelMonth={t('Month')}
                        height="40px"
                        widthOuter='270px'
                        widthInner='120px'
                        flip={true}
                    />
                </Box>
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'center',
                    mt: 3
                }}>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PdfIcon />}
                        onClick={handleDownloadPdf}
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                            color: 'white',
                            px: 3,
                            py: 1.5,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                            },
                            '&:disabled': {
                                background: 'rgba(0, 0, 0, 0.12)'
                            }
                        }}
                    >
                        {t('DownloadPDF') || 'Download PDF'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ExcelIcon />}
                        onClick={handleDownloadExcel}
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                            color: 'white',
                            px: 3,
                            py: 1.5,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                            },
                            '&:disabled': {
                                background: 'rgba(0, 0, 0, 0.12)'
                            }
                        }}
                    >
                        {t('DownloadExcel') || 'Download Excel'}
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    {t('Close') || 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
